import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalOpened } from 'record-it-components/hooks/useModalOpened/useModalOpened';
import {
  HeaderConfig,
  ManageEntitiesContentLayout
} from 'record-it-components/pageContentLayout/ManageEntitiesContentLayout/ManageEntitiesContentLayout';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { EntityName } from 'record-it-time-common/EntityName';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { User } from '../../../classes/entities/User/userTypes';
import { useEmptyCreateEntityButtonConfig } from '../../../hooks/useEmptyCreateEntityButtonConfig';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { CreateUserDialog } from '../../user/CreateUserDialog/CreateUserDialog';
import { UserList } from '../../user/UserList/UserList';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';

export type UserAdminPageProps = PropsWithoutChildren<Record<never, never>>;

export const UserAdminPage: FunctionComponent<UserAdminPageProps> = React.memo(
  (props) => {
    prepareProps({ props, ignoreChildren: true });

    useConfigureBreadcrumbsWithBuilder((builder) =>
      builder.addHomePage().addUserAdminPage()
    );

    const { t } = useTranslation();
    const users = useEntities(EntityName.User, 'getAll');
    const {
      open: openCreateUserDialog,
      close: closeCreateUserDialog,
      modalProps: createUserModalProps
    } = useModalOpened({ closeOnBackDropClick: false });

    const createEntityButtonConfig = useEmptyCreateEntityButtonConfig({
      onCreateEntity: openCreateUserDialog,
      text: t('components.page.UserAdminPage.createEntity')
    });

    const headerConfig: HeaderConfig = useMemo(() => {
      return {
        iconConfig: iconConfigurations.prefixed.page.UserAdminPage.titleIcon,
        title: t('components.page.UserAdminPage.pageTitle')
      };
    }, [t]);

    const renderEntities = useCallback((currentPageUsers: Array<User>) => {
      return <UserList users={currentPageUsers}></UserList>;
    }, []);

    return (
      <>
        <ManageEntitiesContentLayout<User, never>
          createEntityButtonConfig={createEntityButtonConfig}
          headerConfig={headerConfig}
          entities={users}
          renderEntities={renderEntities}
        ></ManageEntitiesContentLayout>
        <CreateUserDialog
          {...createUserModalProps}
          onCancel={closeCreateUserDialog}
          onUserCreated={closeCreateUserDialog}
        ></CreateUserDialog>
      </>
    );
  }
);
